// JOGI DÁTUM-MEGJELENÍTÉS — kanonikus forrás (CLAUDE.md 10. szabály).
//
// MIÉRT ÚJ FOGALOM (2026-10-06, ClickUp 123qmzqzk1r §10):
// a repóban eddig nem volt hosszú, évszámos magyar dátum. Ami volt:
//   entryDate.ts formatEntryDateLabel  -> "aug. 8."      (rövid, év nélkül)
//   Fooldal.tsx:306                    -> "október 31., hétfő" (inline, év nélkül)
//   GlpRiportPdf.tsx                   -> "2026. 10. 31." (numerikus)
// A jogi szövegben és a modálban viszont a HATÁLYBALÉPÉS dátuma áll, amit
// félreérthetetlenül, évvel együtt kell kiírni: "2026. október 31.".
//
// ⚠️ A J8 döntés KÖTELEZŐVÉ teszi, hogy a dátum MINDENHOL a config
// `effectiveFrom` értékéből renderelődjön — kézi dátum a szövegekben NINCS.
// Ezért van szükség egy kanonikus formázóra: ha két helyen külön
// fogalmaznánk meg, a modál és a jogi lap fejléce elcsúszhatna egymástól,
// és pont a dátum az, amin egy jogi vita eldőlne.

/** A hónapnevek SZÁNDÉKOSAN kézzel, nem `toLocaleDateString`-gel.
 *
 * ⚠️ Indok: a natív WebView ICU-adata eszközönként eltér, és a magyar
 * hónapnév `Intl`-ből néhány platformon nagy kezdőbetűs vagy toldalékolt
 * alakot ad. Egy jogi dokumentum hatálybalépési dátuma nem lehet
 * platform-függő. (Ugyanaz a hibaosztály, mint a `daily-check`
 * CET/CEST-bélyegénél: ott az `Intl` `GMT+2`-t adott `CEST` helyett.) */
const HONAPOK = [
  "január", "február", "március", "április", "május", "június",
  "július", "augusztus", "szeptember", "október", "november", "december",
] as const;

/** Egy "YYYY-MM-DD" string szétszedve — `null`, ha nem érvényes naptári nap. */
function parseIsoDay(iso: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso || "").trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  // Naptári érvényesség (pl. 2026-02-30 kiszűrése): UTC-ben építünk, mert
  // itt nincs időzóna-jelentés — puszta naptári nap.
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) {
    return null;
  }
  return { y, m: mo, d };
}

/**
 * Hosszú magyar dátum: `"2026-10-31"` → `"2026. október 31."`
 *
 * Érvénytelen bemenetre ÜRES STRINGET ad, nem dob és nem talál ki dátumot.
 * ⚠️ Indok: ez a függvény a jogi modálban és a jogi lap fejlécében fut. Egy
 * kitalált vagy "Invalid Date" dátum rosszabb, mint a hiánya — a hívó
 * oldalnak kell eldöntenie, mit mutat üres érték helyett.
 */
export function formatLegalDate(iso: string): string {
  const p = parseIsoDay(iso);
  if (!p) return "";
  return `${p.y}. ${HONAPOK[p.m - 1]} ${p.d}.`;
}

/**
 * A jogi lapok és a modál fejléc-sora (J11):
 * `"Hatályos: 2026. október 31. · Verzió: 2026-10-31"`
 *
 * ⚠️ A verzió-azonosító MAGA a hatálybalépés dátuma (ClickUp 123qmzqzk1r
 * §12), ezért egy bemenet elég. Ha valaha elválnának, ez a függvény az
 * egyetlen hely, ahol a két érték szétszedhető — a hívók nem fűzik össze
 * maguk a sort.
 */
export function formatLegalHeaderLine(version: string): string {
  const human = formatLegalDate(version);
  if (!human) return "";
  return `Hatályos: ${human} · Verzió: ${version.trim()}`;
}

/** Két naptári nap közti eltérés NAPBAN (`b - a`), időzóna-mentesen.
 *
 * ⚠️ `Date.UTC`-vel számol, nem helyi idővel: a nyári időszámítás váltása
 * egy helyi idejű kivonásban 23 vagy 25 órás napot adna, és egy 7 napos
 * banner-elzárás így 6 vagy 8 napnak látszana. A bemenet puszta naptári
 * nap, tehát nincs mit időzónázni. */
export function daysBetween(a: string, b: string): number | null {
  const pa = parseIsoDay(a);
  const pb = parseIsoDay(b);
  if (!pa || !pb) return null;
  const ms = Date.UTC(pb.y, pb.m - 1, pb.d) - Date.UTC(pa.y, pa.m - 1, pa.d);
  return Math.round(ms / 86400000);
}
